<template>
  <transition name="q-transition--fade">
    <teleport
      v-if="isOpen"
      to="#app"
    >
      <div
        ref="rootRef"
        :class="['feature-tooltip', `feature-tooltip--${position}`, tooltipWrapperClass]"
        :style="{
          top: tooltipPosition.top + 'px',
          left: tooltipPosition.left + 'px',
          maxWidth,
          '--arrow-width': arrowWidthStr,
          '--arrow-vertical-position': arrowVerticalPositionStr,
        }"
      >
        <div class="feature-tooltip__content-wrapper">
          <slot />
        </div>
      </div>
    </teleport>
  </transition>
</template>

<script lang="ts">
import {
  defineComponent,
  getCurrentInstance,
  reactive,
  ref,
  unref,
  watch,
  useEventListener,
  computed,
  useResizeObserver,
  onBeforeUnmount,
} from '@/composition/vue/compositionApi'
import { TmComponentError } from '@/core/error/TmComponentError'
import { rafThrottle } from '@/utils/rafThrottle'

export default defineComponent({
  props: {
    maxWidth: {
      type: String,
      default: '310px',
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    tooltipWrapperClass: {
      type: String,
      default: '',
    },
    target: {
      type: HTMLElement,
    },
    position: {
      type: String as () => 'left' | 'right' | 'bottom' | 'bottom-left',
      default: 'right',
      validator: (value: string) => ['left', 'right', 'bottom', 'bottom-left'].includes(value),
    },
    arrowVerticalPosition: {
      type: String as () => 'top' | 'middle' | 'bottom',
      default: 'middle',
      validator: (value: string) => ['top', 'middle', 'bottom'].includes(value),
    },
    zIndex: {
      type: Number,
      default: 6001,
    },
  },
  setup(props) {
    const rootRef = ref<HTMLDivElement>()
    const parentRef = ref<HTMLElement>()
    const vm = getCurrentInstance()
    const tooltipPosition = reactive({
      top: 0,
      left: 0,
    })
    const arrowWidth = 12
    const arrowWidthStr = ref(`${arrowWidth}px`)
    const arrowTopPosition = 10
    // distance from the tooltip left edge to the arrow center (for 'bottom-left')
    const arrowLeftOffset = 24

    const arrowVerticalPositionStr = computed(() => {
      switch (props.arrowVerticalPosition || 'middle') {
        case 'top':
          return `${arrowTopPosition}px`
        case 'bottom':
          return 'calc(100% - 20px)'
        case 'middle':
        default:
          return '50%'
      }
    })

    const getParent = () => {
      const parent = unref(parentRef)
      if (!parent) {
        throw new TmComponentError('parentRef not found')
      }
      return parent
    }

    const findParent = () => {
      if (props.target) {
        return props.target
      }

      let item = vm
      while (!(item?.vnode.el instanceof HTMLElement) && item !== null) {
        item = item.parent
      }
      return item?.vnode.el as HTMLElement
    }

    const updatePosition = (initial = false) => {
      const parent = getParent()
      if (!parent) {
        return
      }
      const { top, left, right, bottom, height: parentHeight, width: parentWidth } = parent.getBoundingClientRect()
      const selfHeight = rootRef.value?.offsetHeight ?? 0
      const selfWidth = rootRef.value?.offsetWidth ?? 0

      // bottom positions - tooltip below the target, arrow pointing up
      if (props.position === 'bottom' || props.position === 'bottom-left') {
        tooltipPosition.top = bottom + arrowWidth
        tooltipPosition.left =
          props.position === 'bottom-left'
            ? // arrow near the left edge points at the target center, tooltip extends to the right
              left + parentWidth / 2 - arrowLeftOffset
            : // arrow centered on the target
              left + parentWidth / 2 - selfWidth / 2
        return
      }

      // base position - base position for arrow in the center of the target
      const baseTop = top - selfHeight / 2 + parentHeight / 3

      // calculate offset based on the difference in arrow positions
      const arrowMiddlePosition = selfHeight / 2 // center of the tooltip
      const arrowBottomPosition = selfHeight - arrowTopPosition // position for 'bottom'

      switch (props.arrowVerticalPosition) {
        case 'top': {
          // move tooltip down by the difference between the center and the top position
          const topOffset = arrowMiddlePosition - arrowTopPosition
          tooltipPosition.top = baseTop + topOffset
          break
        }
        case 'bottom': {
          // move tooltip up by the difference between the center and the bottom position
          const bottomOffset = arrowBottomPosition - arrowMiddlePosition
          tooltipPosition.top = baseTop - bottomOffset
          break
        }
        case 'middle':
        default:
          // use base position
          tooltipPosition.top = baseTop
          break
      }

      if (props.position === 'right') {
        const leftDiff = tooltipPosition.left - right
        tooltipPosition.left = right + (initial ? 0 : leftDiff)
      } else {
        const targetLeft = left - selfWidth - arrowWidth
        const rightDiff = tooltipPosition.left - targetLeft
        tooltipPosition.left = targetLeft + (initial ? 0 : rightDiff)
      }
    }

    useEventListener(
      document,
      'scroll',
      (evt) => {
        if (
          !props.isOpen ||
          !(evt.target instanceof HTMLElement) ||
          !parentRef.value ||
          !evt.target.contains(parentRef.value)
        ) {
          return
        }
        updatePosition()
      },
      { capture: true },
    )

    useResizeObserver(parentRef, () => {
      if (props.isOpen) {
        updatePosition(true)
      }
    })

    const requestUpdatePosition = rafThrottle(() => {
      if (props.isOpen) {
        updatePosition(true)
      }
    })
    useEventListener(window, 'resize', requestUpdatePosition)

    watch(
      [() => props.isOpen, () => props.target],
      ([isOpen, target]) => {
        if (isOpen && target) {
          parentRef.value = findParent()
          setTimeout(() => {
            updatePosition(true)
          })
        }
      },
      {
        immediate: true,
      },
    )

    onBeforeUnmount(() => requestUpdatePosition.cancel())

    return {
      rootRef,
      tooltipPosition,
      arrowWidthStr,
      arrowVerticalPositionStr,
    }
  },
})
</script>

<style lang="scss" scoped>
.feature-tooltip {
  position: absolute;
  z-index: v-bind(zIndex);

  &__content-wrapper {
    border-radius: 9px 9px $border-radius-md $border-radius-md;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.24);
    overflow: hidden;
    background-color: white;
  }

  // Right position (default) - arrow to the left of the tooltip
  &--right {
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-style: solid;
      pointer-events: none;
      top: var(--arrow-vertical-position, 50%);
      right: 100%;
      bottom: unset;
      left: unset;
      border-color: transparent $white transparent transparent;
    }

    &::before {
      border-width: 5px 7px;
    }

    &::after {
      margin-right: -1px;
      border-width: 7px var(--arrow-width, 12px);
    }
  }

  // Left position - arrow to the right of the tooltip
  &--left {
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-style: solid;
      pointer-events: none;
      top: var(--arrow-vertical-position, 50%);
      left: 100%;
      bottom: unset;
      right: unset;
      border-color: transparent transparent transparent $white;
    }

    &::before {
      border-width: 5px 7px;
    }

    &::after {
      margin-left: -1px;
      border-width: 7px var(--arrow-width, 12px);
    }
  }

  // Bottom positions - arrow on top of the tooltip, pointing up
  &--bottom,
  &--bottom-left {
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-style: solid;
      pointer-events: none;
      bottom: 100%;
      top: unset;
      border-color: transparent transparent $white transparent;
    }

    &::before {
      border-width: 7px 5px;
    }

    &::after {
      margin-bottom: -1px;
      border-width: var(--arrow-width, 12px) 7px;
    }
  }

  // Bottom (centered) - arrow horizontally centered
  &--bottom {
    &::before,
    &::after {
      left: 50%;
      right: unset;
      transform: translateX(-50%);
    }
  }

  // Bottom-left - arrow near the left edge
  &--bottom-left {
    &::before,
    &::after {
      left: 17px;
      right: unset;
      transform: none;
    }
  }
}

.tm-tooltip {
  &__feature {
    .tm-tooltip__main {
      overflow: hidden;
      padding: 0;
    }
  }
}
</style>
